<template>
	<ja-modal ref="modal" :show="show">
		<content>
			<h1>{{ $t('creditCardPaymentModal') }}</h1>
			<div class="container">
				<div class="img-container">
					<img
						alt=""
						class="credit-cards-image"
						:src="require(`@/assets/images/ccards.png`)"
					/>
				</div>
				<div class="payment-amount-container">
					<div class="modal-text-field bold">
						<span>{{ $t('balance') }}</span>
						<span name="balanceCreditCard">
							{{ balanceCreditCard | currency }}</span
						>
					</div>
					<div v-if="lifeStageIsYA" class="modal-text-field uppercase">
						<span>{{ $t('startingBalance') }} </span>
						<span name="startingBalance">
							{{ creditCardStartingBalance | currency }}</span
						>
					</div>
					<div class="modal-text-field uppercase">
						<span>{{ $t('purchases') }}</span
						><span name="purchases">{{ purchases | currency }}</span>
					</div>
					<div class="modal-text-field uppercase">
						<span>{{ $t('interest') }}</span
						><span name="creditCardInterest">{{
							creditCardInterest | currency
						}}</span>
					</div>
				</div>
				<div class="payment-amount-container">
					<div class="modal-text-field bold">
						<span>{{ $t('minimumPaymentDue') }}</span>
						<span class="minimumPaymentDue">{{
							minimumPaymentDue | currency
						}}</span>
					</div>

					<label
						for="monthly-payment-amount"
						class="label-monthly-payment-amount"
					>
						{{ $t('amountToPay') }}</label
					>
					<ja-currency-input
						id="monthly-payment-amount"
						v-model="amountToPay"
						name="monthly-payment-amount"
						class="input-monthly-payment-amount"
						required
						autocomplete="off"
					>
					</ja-currency-input>

					<ja-alert-bar
						class="alert-bar-payment-amount"
						:alert-condition="alertForLowerAmount"
						>{{ $t('errorMessageLowerAmount') }}</ja-alert-bar
					>

					<ja-dropdown
						class="payment-method disabled"
						:label="$t('paymentMethod')"
						:options="paymentMethods"
						name="paymentMethod"
						:placeholder="
							JA_LOCALIZATION_IRL ? $t('debitAccount') : $t('checkingAccount')
						"
					></ja-dropdown>

					<div class="modal-text-field">
						<span>{{
							JA_LOCALIZATION_IRL ? $t('debitAccount') : $t('checkingAccount')
						}}</span>
						<span
							:name="
								JA_LOCALIZATION_IRL ? $t('debitAccount') : $t('checkingAccount')
							"
							>{{ checkingAccount | currency }}
						</span>
					</div>

					<ja-alert-bar
						class="alert-bar-checking-account"
						:alert-condition="alertForNotEnoughMoneyInAccount"
						>{{
							$t('noticeCreditForNotEnoughMoneyInChechingAccount')
						}}</ja-alert-bar
					>
				</div>
			</div>

			<div class="btn-container">
				<ja-button
					v-if="showMakePaymentBtn"
					class="small"
					name="makePayment"
					:disabled="!checkIfMontholyPaymentIsValid()"
					@click.native="makePayment"
				>
					{{ $t('makePayment') }}</ja-button
				>
				<ja-button
					v-if="!showMakePaymentBtn"
					class="small"
					name="makePaymentWithoutPayment"
					@click.native="saveCreditCardPayment"
				>
					{{ $t('okay') }}</ja-button
				>
			</div>
		</content>
	</ja-modal>
</template>
<script>
import gql from 'graphql-tag';
import Modal from './Modal';
import Button from '../ui/Button';
import CurrencyInput from '../../components/ui/CurrencyInput';
import Dropdown from '../../components/ui/form/Dropdown';
import LifeStageData from '@/mixins/LifeStageData';
import ModalEvents from '@/mixins/ModalEvents';
import ProfileData from '@/mixins/ProfileData';
import AlertBar from '../../components/ui/AlertBar';

export default {
	components: {
		jaModal: Modal,
		jaButton: Button,
		jaCurrencyInput: CurrencyInput,
		jaDropdown: Dropdown,
		jaAlertBar: AlertBar
	},
	mixins: [LifeStageData, ProfileData, ModalEvents],

	props: {
		show: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			ifLifeStageNameYA: false,
			showMakePaymentBtn: true,
			JA_LOCALIZATION_IRL: this.$store.getters.isIrl,
			amountToPay: null,
			alertForLowerAmount: false,
			alertForNotEnoughMoneyInAccount: false,
			paymentType: 'CreditCard',
			percentMinPayment: 0.02
		};
	},
	computed: {
		paymentMethods() {
			return [
				{
					name: `${
						this.JA_LOCALIZATION_IRL
							? this.$t('debitAccount')
							: this.$t('checkingAccount')
					}`,
					value: `${
						this.JA_LOCALIZATION_IRL
							? this.$t('debitAccount')
							: this.$t('checkingAccount')
					}`
				}
			];
		},
		userData() {
			return this.$store.getters.userData;
		},
		purchases() {
			return this.lifeStageIsYA
				? this.lifeStageData.creditCardDebt - this.creditCardStartingBalance
				: this.lifeStageData.creditCardDebt;
		},
		checkingAccount() {
			return this.netMonthlyIncome - this.lifeStageData.totalExpenses;
		},
		balanceCreditCard() {
			let balance = this.purchases + this.creditCardInterest;
			if (this.lifeStageIsYA) {
				balance += this.creditCardStartingBalance;
			}
			return balance;
		},
		creditCardInterest() {
			return this.lifeStageData.creditCardInterest;
		},
		minimumPaymentDue() {
			return this.lifeStageData.minimumStudentCreditCardPayment;
		},
		creditCardLimit() {
			return this.profileData.cmsLifeStage.creditCardLimit;
		},
		creditCardScore() {
			return this.profileData.currentCreditScore;
		},
		validAmountToPay() {
			return this.amountToPay >= this.minimumPaymentDue;
		},
		validIfEnoughInCheckingAccount() {
			return (
				this.checkingAccount >= Math.round(this.minimumPaymentDue) &&
				this.amountToPay <= this.checkingAccount &&
				this.amountToPay >= Math.round(this.minimumPaymentDue)
			);
		}
	},
	watch: {
		amountToPay() {
			this.checkIfMontholyPaymentIsValid();
		}
	},
	methods: {
		checkIfMontholyPaymentIsValid() {
			if (this.checkingAccount >= Math.round(this.minimumPaymentDue)) {
				return this.validIfEnoughInCheckingAccount;
			} else {
				return this.validAmountToPay;
			}
		},
		async makePayment() {
			if (this.checkIfMontholyPaymentIsValid()) {
				if (this.amountToPay > this.checkingAccount) {
					this.showMakePaymentBtn = false;
					this.alertForNotEnoughMoneyInAccount = true;
				} else {
					const payedAmount = {
						payedAmount: this.amountToPay,
						lifeStage_ID: this.lifeStageData.lifeStage_ID,
						paymentType: this.paymentType,
						studentId: this.userData.studentId
					};

					const response = await this.$apollo.mutate({
						mutation: gql`
							mutation AddStudentLoan(
								$add: StudentPaymentAddInputObjectGraphType!
							) {
								addStudentPayment(add: $add) {
									id
								}
							}
						`,
						variables: {
							add: payedAmount
						}
					});
					if (response.data) {
						this.finishCreditCardPayment();
					}
				}
			} else {
				this.alertForLowerAmount = true;
				this.$announcer.assertive(
					`Error: ${this.$t('errorMessageLowerAmount')}`
				);
			}
		},

		async saveCreditCardPayment() {
			const studentCreditScore = {
				id: this.profileData.studentCreditScores[0].id,
				score: this.creditCardScore - 50
			};
			const response = await this.$apollo.mutate({
				mutation: gql`
					mutation($update: StudentCreditScoreInputObjectGraphType!) {
						updateStudentCreditScore(update: $update) {
							score
						}
					}
				`,
				variables: {
					update: studentCreditScore
				}
			});
			if (response.data) {
				this.finishCreditCardPayment();
			}
		},

		async finishCreditCardPayment() {
			this.$store.commit('setLoading', true);

			const studentLoanInfo = {
				id: this.userData.studentId,
				hideCreditCardPaymentPopup: true
			};
			const response = await this.$apollo.mutate({
				mutation: gql`
					mutation($update: StudentInputObjectGraphType!) {
						updateIgnoreNullValuesStudent(update: $update) {
							hideCreditCardPaymentPopup
						}
					}
				`,
				variables: {
					update: studentLoanInfo
				}
			});
			if (response.data) {
				this.$store.commit('setLoading', false);
				this.$emit('closecreditCardLoanModal');
			}
		}
	}
};
</script>
<style lang="scss" scoped>
.container {
	height: auto;
	width: 100%;
	border-top: 1px solid $button-border;
	border-bottom: 1px solid $button-border;

	.img-container {
		.credit-cards-image {
			margin: 33px auto;
		}
	}

	.payment-amount-container {
		width: 70%;
		margin: auto;
		padding-top: 20px;
		padding-bottom: 20px;
		display: flex;
		flex-direction: column;

		&:nth-child(3) {
			border-top: 1px solid $button-border;
		}

		.label-monthly-payment-amount {
			font-family: 'RobotoMedium';
			font-size: 12px;
			text-transform: uppercase;
			text-align: left;
			color: #687682;
			font-weight: 500;
			letter-spacing: 1px;
			margin-top: 20px;

			@media (min-width: $breakpoint-mid) {
				font-size: 14px;
			}
		}

		.input-monthly-payment-amount {
			margin-bottom: 15px;
		}
		.alert-bar-payment-amount,
		.alert-bar-checking-account {
			word-break: break-word;
			border: 1px solid $alert-container-border-color;
			border-radius: 4px;
			padding: 8px;
			margin-top: 20px;
			margin-bottom: 20px;
		}
	}
}

.btn-container {
	height: 76px;
	display: flex;
	align-items: center;

	.make-payment-btn {
		display: block;

		&:hidden {
			display: none;
		}
	}

	.okay-payment-btn {
		display: none;

		&:visible {
			display: block;
		}
	}
}
.modal-text-field {
	display: flex;
	justify-content: space-between;
	color: $icon-done;
	line-height: 19px;
	padding-top: 14px;

	&.bold {
		color: $font-header;
		font-family: 'RobotoRegular';
		font-size: 16px;
		padding-bottom: 24px;

		@media (min-width: $breakpoint-mid) {
			font-size: 18px;
		}
	}

	&.uppercase {
		text-transform: uppercase;
		font-family: 'RobotoMedium';
		font-size: 12px;
		padding-bottom: 7px;
		padding-top: 7px;

		@media (min-width: $breakpoint-mid) {
			font-size: 14px;
		}
	}
}
</style>
